package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Reusable confirmation dialog for a discrete, reversible-only-with-effort action (e.g. delete).
 * **Copy-free**: title, message, and both button labels are passed in by the caller so every surface
 * supplies its own localized copy. Set [destructive] to tint the confirm button as a warning.
 *
 * Pass [onInputChange] to add an optional free-text field under the message (e.g. an admin's
 * rejection reason) — [inputValue]/[inputPlaceholder] accompany it; all copy stays caller-supplied.
 */
@Composable
fun AppConfirmDialog(
    title: String,
    message: String,
    confirmLabel: String,
    cancelLabel: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    destructive: Boolean = false,
    inputValue: String = "",
    onInputChange: ((String) -> Unit)? = null,
    inputPlaceholder: String? = null,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = AppTheme.colors.surface,
        titleContentColor = AppTheme.colors.onSurface,
        textContentColor = AppTheme.colors.onSurfaceVariant,
        shape = AppTheme.shapes.card,
        title = {
            Text(text = title, style = AppTheme.typography.headlineMd, color = AppTheme.colors.onSurface)
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                Text(text = message, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant)
                if (onInputChange != null) {
                    AppTextField(
                        value = inputValue,
                        onValueChange = onInputChange,
                        // Label (not placeholder) so the field stays described once typing starts
                        // (a11y), and multiline for longer notes like rejection reasons.
                        label = inputPlaceholder,
                        singleLine = false,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        },
        confirmButton = {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppButton(
                    text = cancelLabel,
                    onClick = onDismiss,
                    variant = AppButtonVariant.Text,
                )
                AppButton(
                    text = confirmLabel,
                    onClick = onConfirm,
                    variant = if (destructive) AppButtonVariant.Secondary else AppButtonVariant.Primary,
                    modifier = Modifier,
                )
            }
        },
    )
}
